import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { calculateMultiplicationInside } from '../../helpers';
import { Box } from '../containers';
import arrowLeftBlue from '../../img/arrow-left-blue.svg';
import arrowLeftGrey from '../../img/arrow-left-grey.svg';
import arrowRightBlue from '../../img/arrow-right-blue.svg';
import arrowRightGrey from '../../img/arrow-right-grey.svg';
import ellipsisBlue from '../../img/ellipsis-blue.svg';
import ellipsisGrey from '../../img/ellipsis-grey.svg';
import s from './Pagination.css';

const propTypes = {
    className: PropTypes.string,
    onPageChange: PropTypes.func.isRequired,
    currentPage: PropTypes.number.isRequired,
    countPerPage: PropTypes.number.isRequired,
    countTotal: PropTypes.number.isRequired,
    displayTotals: PropTypes.bool,
};

export class Pagination extends React.Component {
    static defaultProps = {
        className: undefined,
        displayTotals: true,
    };

    constructor(props) {
        super(props);

        this.state = {
            edgesCount: 3,
            showingFrom: 1,
            showingTo: this.props.countPerPage,
            currentPage: this.props.currentPage,
            totalPages: calculateMultiplicationInside(this.props.countTotal, this.props.countPerPage),
            pagesToDisplay: [1],
            displayEllipsisAtStart: false,
            displayEllipsisAtEnd: false,
        };
    }

    componentDidMount() {
        this.setPagesToDisplay();
    }

    componentWillReceiveProps(nextProps) {
        // Check if total count or count per page has changed
        if (this.props.countTotal !== nextProps.countTotal || this.props.countPerPage !== nextProps.countPerPage) {
            // Calculate new total pages
            const totalPages = calculateMultiplicationInside(nextProps.countTotal, nextProps.countPerPage);
            this.setPagesToDisplay(nextProps.currentPage, totalPages, nextProps.countTotal);
        } else if (
            this.props.currentPage !== nextProps.currentPage &&
            this.state.currentPage !== nextProps.currentPage
        ) {
            this.switchToPage(nextProps.currentPage);
        }
    }

    render() {
        return (
            <Box className={classNames(s.pagination, this.props.className)}>
                {this.props.countTotal > 0 ? this.renderPages() : this.renderEmpty()}
            </Box>
        );
    }

    renderEmpty() {
        return (
            <div className={s.container}>
                <p className={s.paginationTotals}>No entries available</p>
            </div>
        );
    }

    renderPages() {
        const firstPageToDisplay = this.state.pagesToDisplay[0];
        const lastPageToDisplay = this.state.pagesToDisplay[this.state.pagesToDisplay.length - 1];

        return (
            <div className={s.container}>
                {this.props.displayTotals && (
                    <div className={s.column}>
                        <p className={s.paginationTotals}>
                            {'Showing entries '}
                            <strong>{this.state.showingFrom}</strong>
                            {' — '}
                            <strong>{this.state.showingTo}</strong>
                            {' from total of '}
                            <strong>{this.props.countTotal}</strong>
                        </p>
                    </div>
                )}

                <div className={s.column}>
                    <ul className={s.paginationLinksList}>
                        <li className={s.paginationArrow}>
                            <button onClick={this.handlePageClick(this.state.currentPage - 1)}>
                                <img src={this.state.currentPage <= 1 ? arrowLeftGrey : arrowLeftBlue} alt="" />
                            </button>
                        </li>

                        {this.state.displayEllipsisAtStart &&
                            firstPageToDisplay > 1 && (
                                <li className={s.paginationPage}>
                                    <button onClick={this.handlePageClick(1)}>1</button>
                                </li>
                            )}

                        {this.state.displayEllipsisAtStart &&
                            firstPageToDisplay > 1 && (
                                <li className={s.paginationEllipsis}>
                                    <button onClick={this.handlePageClick(firstPageToDisplay - 1)}>
                                        <img src={ellipsisGrey} alt="" />
                                    </button>
                                </li>
                            )}

                        {this.state.pagesToDisplay.map(page => (
                            <li
                                key={page}
                                className={classNames(s.paginationPage, {
                                    [s.paginationPageActive]: this.state.currentPage === page,
                                })}
                            >
                                <button onClick={this.handlePageClick(page)}>{page}</button>
                            </li>
                        ))}

                        {this.state.displayEllipsisAtEnd &&
                            lastPageToDisplay < this.state.totalPages && (
                                <li className={s.paginationEllipsis}>
                                    <button onClick={this.handlePageClick(lastPageToDisplay + 1)}>
                                        <img src={ellipsisGrey} alt="" />
                                    </button>
                                </li>
                            )}

                        {this.state.displayEllipsisAtEnd &&
                            lastPageToDisplay < this.state.totalPages && (
                                <li className={s.paginationPage}>
                                    <button onClick={this.handlePageClick(this.state.totalPages)}>
                                        {this.state.totalPages}
                                    </button>
                                </li>
                            )}

                        <li className={s.paginationArrow}>
                            <button onClick={this.handlePageClick(this.state.currentPage + 1)}>
                                <img
                                    src={
                                        this.state.currentPage >= this.state.totalPages
                                            ? arrowRightGrey
                                            : arrowRightBlue
                                    }
                                    alt=""
                                />
                            </button>
                        </li>
                    </ul>
                </div>
            </div>
        );
    }

    handlePageClick = newPage => e => {
        this.switchToPage(newPage);
    };

    switchToPage = newPage => {
        // Check if new page is smaller than first page or larger than last page
        newPage = newPage <= 0 ? this.state.totalPages : newPage > this.state.totalPages ? 1 : newPage;

        // Change current page
        this.setPagesToDisplay(newPage);

        // Pass event further
        if (this.props.onPageChange) {
            this.props.onPageChange(newPage);
        }
    };

    setPagesToDisplay = (newPage, newTotalPages, newTotalCount) => {
        // Get total pages
        const totalPages = typeof newTotalPages !== 'undefined' ? newTotalPages : this.state.totalPages;

        // Check if newPage is provided
        if (typeof newPage === 'undefined' || newPage === null || newPage === 0) {
            newPage = this.state.currentPage;
        }

        // Get total count
        const totalCount = typeof newTotalCount !== 'undefined' ? newTotalCount : this.props.countTotal;

        // Initially
        let displayEllipsisAtStart = false;
        let displayEllipsisAtEnd = false;
        let pagesToDisplay = [newPage];

        // Add edges
        if (this.state.edgesCount) {
            for (let i = 0; i < this.state.edgesCount; i++) {
                if (pagesToDisplay[0] > 1) {
                    pagesToDisplay.unshift(pagesToDisplay[0] - 1);
                }

                if (pagesToDisplay[pagesToDisplay.length - 1] < totalPages) {
                    pagesToDisplay.push(pagesToDisplay[pagesToDisplay.length - 1] + 1);
                }
            }
        }

        // Check if ellipsis should show at the start
        if (pagesToDisplay[0] > 2) {
            displayEllipsisAtStart = true;
        }

        // Check if ellipsis should show at the end
        if (pagesToDisplay[pagesToDisplay.length - 1] - 1 < totalPages) {
            displayEllipsisAtEnd = true;
        }

        // End of showing entries
        let showingTo = newPage * this.props.countPerPage;
        const showingFrom = showingTo - this.props.countPerPage + 1;
        showingTo = showingTo > totalCount ? totalCount : showingTo;

        // Set new state
        this.setState({
            totalPages,
            currentPage: newPage,
            pagesToDisplay,
            showingFrom,
            showingTo,
            displayEllipsisAtStart,
            displayEllipsisAtEnd,
        });
    };
}

Pagination.propTypes = propTypes;



// WEBPACK FOOTER //
// ./app/web/src/components/actions/Pagination.jsx